import * as t from 'io-ts';
import { optional } from './helper';

const teamInfo = t.type({
  icon: optional(t.string),
  shortName: optional(t.string),
  sponsorshipName: optional(t.string),
  mobiName: optional(t.string),
  countryCode: optional(t.string),
  countryISO3: optional(t.string),
  name: optional(t.string),
  teamCode: optional(t.string),
  teamId: optional(t.string),
  sportsteamId: optional(t.string),
});

const logInfo = t.type({
  wins: t.number,
  losses: t.number,
  draws: t.number,
  points: t.number,
  gamesPlayed: t.number,
  goalsForOverall: t.number,
  goalsAgainstOverall: t.number,
  goalsDifference: t.number,
});

const teams = t.type({
  team: teamInfo,
  logs: logInfo,
});

const divisions = t.type({
  name: optional(t.string),
  abbreviation: optional(t.string),
  teams: t.array(teams),
});

const conferences = t.type({
  name: optional(t.string),
  abbreviation: optional(t.string),
  divisions: t.array(divisions),
});

const tournamentInfo = t.type({
  name: optional(t.string),
  icon: optional(t.string),
  leaguePath: optional(t.string),
  tournamentId: optional(t.string),
  sportsTourId: optional(t.string),
  sportsSeasonId: optional(t.string),
  tournamentSlug: optional(t.string),
  tournamentName: optional(t.string),
  season: optional(t.string),
});

const footballLogs = t.type({
  tournament: tournamentInfo,
  conferences: t.array(conferences),
});

export const footballLogsApiResponse = footballLogs;
export type FootballTable = t.TypeOf<typeof footballLogsApiResponse>;

const teamDetails = t.type({
  icon: optional(t.string),
  teamId: t.string,
  name: optional(t.string),
  shortName: optional(t.string),
  countryCode: optional(t.string),
  countryName: optional(t.string),
});

const playerInfo = t.type({
  playerId: optional(t.string),
  displayName: optional(t.string),
  shortName: optional(t.string),
  countryCode: optional(t.string),
  countryName: optional(t.string),
});

const mainOrSubstitute = t.type({
  player: playerInfo,
  shirtNumber: optional(t.string),
  position: optional(t.string),
});

const player = t.type({
  main: t.array(mainOrSubstitute),
  substitutes: t.array(mainOrSubstitute),
});

const homeAndAwayTeam = t.type({
  team: teamDetails,
  players: player,
});
export type HomeAndAwayTeam = t.TypeOf<typeof homeAndAwayTeam>;

const lineup = t.type({
  home: homeAndAwayTeam,
  away: homeAndAwayTeam,
});
export type LineUps = t.TypeOf<typeof lineup>;

const footballTournament = t.type({
  leaguePath: optional(t.string),
  tournamentName: optional(t.string),
  tournamentId: optional(t.string),
  tournamentSlug: optional(t.string),
});

const footballLineUp = t.type({
  matchId: optional(t.string),
  tournament: footballTournament,
  lineups: lineup,
});

export const footballLineUpApiResponse = footballLineUp;
export type FootballLineUp = t.TypeOf<typeof footballLineUpApiResponse>;

const footballCommentaries = t.type({
  additionalMinutes: t.string,
  minutes: t.string,
  seconds: t.string,
  message: t.string,
  type: t.string,
});
export type FootballCommentaries = t.TypeOf<typeof footballCommentaries>;

const footballCommentary = t.type({
  matchId: t.string,
  tournament: footballTournament,
  commentaries: t.array(footballCommentaries),
});

export const footballCommentaryApiResponse = footballCommentary;
export type FootballCommentary = t.TypeOf<typeof footballCommentaryApiResponse>;

const statsValues = t.type({
  shots: t.number,
  shotsOnTarget: t.number,
  corners: t.number,
  offsides: t.number,
  fouls: t.number,
  yellowCards: t.number,
  redCards: t.number,
  possessionPercentage: t.number,
});

const homeTeamStats = t.type({
  team: teamDetails,
  values: statsValues,
});

const teamStats = t.type({
  home: homeTeamStats,
  away: homeTeamStats,
});
export type Stats = t.TypeOf<typeof teamStats>;

const matchTime = t.type({
  minutes: t.number,
  additionalMinutes: t.number,
  seconds: t.number,
});

const teamPlayers = t.type({
  entering: playerInfo,
  leaving: playerInfo,
});

const teamEvents = t.type({
  team: teamDetails,
  players: optional(teamPlayers),
  player: optional(playerInfo),
  type: optional(t.string),
  side: optional(t.string),
  time: matchTime,
});
export type StatsEvents = t.TypeOf<typeof teamEvents>;

const footballStats = t.type({
  matchId: t.string,
  tournament: footballTournament,
  stats: teamStats,
  events: t.array(teamEvents),
});
export const footballStatsApiResponse = footballStats;
export type FootballStats = t.TypeOf<typeof footballStatsApiResponse>;
